// Display helpers shared across the admin panel. Turkish locale, Istanbul TZ.

import { LEVEL_LABELS_TR, type Level, trDot } from '@okn/shared';

const LEVEL_ORDER: Level[] = ['ground', 'side_balcony_left', 'side_balcony_right', 'upper_balcony'];

export function fmtDateTime(iso: string): string {
  return trDot(new Date(iso).toLocaleString('tr-TR', { dateStyle: 'short', timeStyle: 'short' }));
}

export function fmtLongDate(iso: string): string {
  return trDot(
    new Intl.DateTimeFormat('tr-TR', {
      timeZone: 'Europe/Istanbul',
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(new Date(iso)),
  );
}

export function fmtTime(iso: string): string {
  return trDot(new Date(iso).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' }));
}

/** "3 dk önce" style relative label for recent audit / activity. */
export function fmtRelative(iso: string, now: number = Date.now()): string {
  const diff = now - new Date(iso).getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'az önce';
  if (min < 60) return `${min} dk önce`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} sa önce`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day} gün önce`;
  return fmtDateTime(iso);
}

export type DisplaySeat = { id?: string; label: string; level: string };

export type SeatGroup = {
  level: Level;
  levelLabel: string;
  rows: Array<{ rowLabel: string; seats: DisplaySeat[] }>;
  count: number;
};

/** Group a reservation's seats by level → row, sorted for human reading. */
export function groupSeats(seats: DisplaySeat[]): SeatGroup[] {
  const byLevel = new Map<string, DisplaySeat[]>();
  for (const s of seats) {
    const arr = byLevel.get(s.level) ?? [];
    arr.push(s);
    byLevel.set(s.level, arr);
  }

  const ordered = [...byLevel.keys()].sort(
    (a, b) => LEVEL_ORDER.indexOf(a as Level) - LEVEL_ORDER.indexOf(b as Level),
  );

  return ordered.map((level) => {
    const levelSeats = byLevel.get(level) ?? [];
    const byRow = new Map<string, DisplaySeat[]>();
    for (const s of levelSeats) {
      const row = s.label.split('-')[0] ?? '?';
      const arr = byRow.get(row) ?? [];
      arr.push(s);
      byRow.set(row, arr);
    }
    const rows = [...byRow.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'tr'))
      .map(([rowLabel, rowSeats]) => ({
        rowLabel,
        seats: rowSeats.sort((a, b) => seatNum(a.label) - seatNum(b.label)),
      }));
    return {
      level: level as Level,
      levelLabel: LEVEL_LABELS_TR[level as Level] ?? level,
      rows,
      count: levelSeats.length,
    };
  });
}

function seatNum(label: string): number {
  const n = Number.parseInt(label.split('-')[1] ?? '0', 10);
  return Number.isNaN(n) ? 0 : n;
}

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
const REF_RE = /\bOKN-?[0-9A-HJKMNP-TV-Z]{4}\b/i;

/**
 * A scanned QR encodes a deep link like
 *   https://…/admin/rezervasyonlar/<uuid>
 * or sometimes just a reference code. Extract whichever identifier is present.
 */
export function parseScan(text: string): { reservationId?: string; reference?: string } {
  const id = text.match(UUID_RE)?.[0];
  if (id) return { reservationId: id };
  const ref = text.match(REF_RE)?.[0];
  if (ref) return { reference: ref.toUpperCase().replace(/^OKN-?/i, 'OKN-') };
  return {};
}
